/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

/**
 * throttle 입니다.
 * @param {Function} callback - 마지막 debounce 호출 이후 delay 이후 호출될 함수.
 * @param {number} delay - delay: millisecond 기준.
 * @example
 * ```ts
 *  const callbackFunc = () => {
 *    return 'callback'
 *  }
 *
 *  const debounce(callback, 300);
 * ```
 */
function debounce(callback, delay) {
    var timeout;
    return function debouncedCallback() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        clearTimeout(timeout);
        timeout = setTimeout(function () {
            callback.apply(void 0, __spreadArray([], __read(args), false));
        }, delay);
    };
}

var BREAK_POINT = {
    MOBILE: 768,
    TABLET: 1100,
};
var mediaQuery = function (width, option) {
    if (option === void 0) { option = 'max'; }
    return "@media (".concat(option, "-width: ").concat(width, "px)");
};
/**
 * media 입니다.
 * @example
 * ```tsx
 * import { media } from '@myrealtrip/utils';
 *
 * const Component = styled.div`
 *   // 모바일 해상도 미디어쿼리
 *   ${media.mobile} {
 *     color: red;
 *   }
 *
 *   ${media.tablet} {
 *   // 테블릿 해상도 미디어쿼리
 *     color: blue;
 *   }
 * `
 *
 * const MobileFirstComponent = styled.div`
 *   // 모바일퍼스트 : 모바일 해상도 미디어 쿼리
 *   ${media.minMobile} {
 *     color: yellow;
 *   }
 * `
 * ```
 */
var media = {
    custom: mediaQuery,
    mobile: mediaQuery(BREAK_POINT.MOBILE),
    tablet: mediaQuery(BREAK_POINT.TABLET),
    minMobile: mediaQuery(BREAK_POINT.MOBILE + 1, 'min'),
    minTablet: mediaQuery(BREAK_POINT.TABLET + 1, 'min'),
};

/**
 * 맵을 인자로 받아서 쿼리스트링을 반환하는 함수입니다. 만약 맵 내부에 유효한 값이 없다면 빈 문자열을 반환합니다.
 *
 * @example
 * ```js
 * stringifyQueryParams({ foo: true, bar: 'hello', baz: '안녕' });
 * // '?foo=true&bar=hello&baz=%EC%95%88%EB%85%95'
 *
 * stringifyQueryParams({ foo: undefined });
 * // ''
 * ```
 */
function stringifyQueryParams(params) {
    if (params === void 0) { params = {}; }
    var queryString = Object.entries(params)
        .filter(function (_a) {
        var _b = __read(_a, 2), value = _b[1];
        return value != null;
    })
        .map(function (_a) {
        var _b = __read(_a, 2), key = _b[0], value = _b[1];
        var encodedValue = encodeURIComponent(value);
        return "".concat(key, "=").concat(encodedValue);
    });
    if (queryString.length === 0) {
        return '';
    }
    return "?".concat(queryString.join('&'));
}
/**
 * 쿼리 스트링을 인자로 받아서 맵을 반환하는 함수입니다.
 * @example
 * parseQueryString('?foo=1&bar=%ED%95%98%EC%9D%B4');
 * // { foo: '1', bar: '하이' }
 */
function parseQueryString(queryString) {
    var queryEntries = queryString.trim().replace(/^\?/, '').split('&');
    var queryParams = queryEntries.map(function (query) { return query.split('='); });
    return queryParams.reduce(function (result, _a) {
        var _b;
        var _c = __read(_a, 2), key = _c[0], value = _c[1];
        return __assign(__assign({}, result), (_b = {}, _b[key] = value ? decodeURIComponent(value) : undefined, _b));
    }, {});
}

/**
 * Create an object using key(s) and a value
 * @param {string[]} keys - compose each key of an object
 * @param {T} value - compose every value of an object
 * @returns {Record<string, T>}
 */
function bindValueToKeys(keys, value) {
    function bindEntries(entries) {
        return entries.reduce(function (map, _a) {
            var _b;
            var _c = __read(_a, 2), key = _c[0], value = _c[1];
            return __assign(__assign({}, map), (_b = {}, _b[key] = value, _b));
        }, {});
    }
    return bindEntries(keys.map(function (key) { return [key, value]; }));
}

var DEFAULT_VALUE = '0';
function attemptInsertComma(input) {
    return "".concat(input).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
var decimalPointMap = {
    roundDown: function (target) { return Math.floor(Number(target)); },
    roundUp: function (target) { return Math.ceil(Number(target)); },
    round: function (target) { return Math.round(Number(target)); },
};
/**
 * Convert any target to currency
 * @param {string | number | null | undefined} target
 * @param {'roundDown' | 'roundUp' | 'round'} [decimalPoint='roundDown'] - roundDown(버림), roundUp(올림), round(반올림)
 * @returns {string}
 */
function toCurrency(target, decimalPoint) {
    if (!target) {
        return DEFAULT_VALUE;
    }
    if (!Number(target)) {
        return "".concat(target);
    }
    var handleDecimalPoint = decimalPointMap[decimalPoint !== null && decimalPoint !== void 0 ? decimalPoint : 'roundDown'];
    return attemptInsertComma(handleDecimalPoint(target));
}
/**
 * Convert number typed target to currency
 * @param {string | number | null | undefined} target
 * @param {'roundDown' | 'roundUp' | 'round'} [decimalPoint='roundDown'] - roundDown(버림), roundUp(올림), round(반올림)
 * @returns {string}
 */
function toCurrencyStrict(target, decimalPoint) {
    if (!target) {
        return DEFAULT_VALUE;
    }
    if (!Number(target)) {
        return DEFAULT_VALUE;
    }
    var handleDecimalPoint = decimalPointMap[decimalPoint !== null && decimalPoint !== void 0 ? decimalPoint : 'roundDown'];
    return attemptInsertComma(handleDecimalPoint(target));
}

/**
 * throttle 입니다.
 * @param {Function} callback - delay(지연) 이후에 실행되는 함수입니다.
 * @param {number} delay - delay(지연)시킬 시간 millisecond 기준.
 * @example
 * ```ts
 *  const callbackFunc = () => {
 *    return 'callback'
 *  }
 *
 *  throttle(callback, 300);
 * ```
 */
function throttle(callback, delay) {
    var isThrottle = false;
    return function throttledCallback() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (isThrottle === false) {
            callback.apply(void 0, __spreadArray([], __read(args), false));
            isThrottle = true;
            setTimeout(function () {
                isThrottle = false;
            }, delay);
        }
    };
}

var CurrencyKor = {
    KRW: '원',
    USD: '달러',
    JPY: '엔',
    GBP: '파운드',
    EUR: '유로',
};
var Symbol$1 = {
    KRW: '₩',
    USD: '$',
    JPY: '¥',
    GBP: '£',
    EUR: '€',
};
/**
 * Return price expression using currency code and symbol
 * @param {number} price
 * @param {'KRW' | 'USD' | 'JPY' | 'GBP' | 'EUR'}  currencyCode
 * @param {{price: number; currencyCode: 'KRW' | 'USD' | 'JPY' | 'GBP' | 'EUR';}} [subPrice]
 * @param {boolean} [isPriceVariable=false] - be used to express estimated price (ex. 약 5,000원)
 * @returns {string}
 */
function toPriceExp(_a) {
    var price = _a.price, currencyCode = _a.currencyCode, subPrice = _a.subPrice, _b = _a.isPriceVariable, isPriceVariable = _b === void 0 ? false : _b;
    return "".concat(isPriceVariable ? '약 ' : '').concat(toCurrency(price)).concat(CurrencyKor[currencyCode]).concat(subPrice ? " (".concat(Symbol$1[subPrice.currencyCode]).concat(toCurrency(subPrice.price), ")") : '');
}

function omit(obj, keys) {
    var cloneObj = __assign({}, obj);
    keys.forEach(function (key) { return delete cloneObj[key]; });
    return cloneObj;
}

export { bindValueToKeys, debounce, media, omit, parseQueryString, stringifyQueryParams, throttle, toCurrency, toCurrencyStrict, toPriceExp };
